import { Alert, Spinner, Table } from 'react-bootstrap';

// delivery_distance_pricing.md - the price breakdown shown on the booking form (and inside the
// confirm popup) BEFORE a booking request is submitted. Fed by getBookingQuote(); the delivery
// charge line is distance-based (owner pickup -> the customer's delivery address), so it only
// resolves once the customer has picked that address from the Maps search / "detect my location".
//
// quote shape (from POST /:vertical/bookings/quote):
//   { deliveryChargeAvailable, nights, rentalAmount, platformFee, deliveryCharge,
//     deliveryChargeBasis, deliveryDistanceText, deliveryFeeLines, depositApplicable, depositAmount,
//     totalAmount, cashOnDelivery }. When deliveryChargeAvailable is false the rental / platform fee
//     / deposit are still priced; only deliveryCharge + totalAmount come back null (pick an address).

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function PriceBreakdown({ quote, loading, error, className = '' }) {
  if (loading) {
    return (
      <div className={`text-muted small ${className}`}>
        <Spinner animation="border" size="sm" role="status" /> Working out the price…
      </div>
    );
  }
  if (error) return <Alert variant="light" className={`py-2 small mb-0 ${className}`}>{error}</Alert>;
  if (!quote) return null;

  const deliveryPending = quote.deliveryChargeAvailable === false;
  const feeLines = quote.deliveryFeeLines || [];

  return (
    <div className={className}>
      <h6 className="text-uppercase text-muted small mb-2">Price</h6>
      <Table size="sm" className="mb-1">
        <tbody>
          <tr>
            <td>
              Rental
              {quote.periods
                ? <span className="text-muted"> · {quote.periods} {quote.periods === 1 ? quote.periodUnit : `${quote.periodUnit}s`}</span>
                : quote.nights ? <span className="text-muted"> · {quote.nights} {quote.nights === 1 ? 'day' : 'days'}</span> : null}
              {/* A week/month listing is booked in whole periods and billed at the full-period
                  rate. The per-day figure is the owner's internal pro-rata, not shown here. */}
              {quote.periods && quote.listPrice != null && (
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {money(quote.listPrice)}/{quote.periodUnit} × {quote.periods}
                </div>
              )}
            </td>
            <td className="text-end">{money(quote.rentalAmount)}</td>
          </tr>
          <tr>
            <td>Platform fee</td>
            <td className="text-end">{money(quote.platformFee)}</td>
          </tr>
          <tr>
            <td>
              Delivery charge
              {!deliveryPending && quote.deliveryChargeBasis === 'FREE_SHIPPING' && (
                <span className="text-muted"> · free shipping</span>
              )}
              {!deliveryPending && quote.deliveryChargeBasis === 'DISTANCE_TIERED' && quote.deliveryDistanceText && (
                <span className="text-muted"> · {quote.deliveryDistanceText}</span>
              )}
              {!deliveryPending && quote.deliveryChargeBasis === 'FLAT_FALLBACK' && (
                <span className="text-muted"> · flat rate</span>
              )}
              {feeLines.length > 1 && (
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {feeLines.map((l) => `${l.label} = ₹${l.amount}`).join('  +  ')}
                </div>
              )}
            </td>
            <td className="text-end">
              {deliveryPending ? <span className="text-muted">—</span> : money(quote.deliveryCharge)}
            </td>
          </tr>
          {quote.depositAmount > 0 && (
            <tr>
              <td>Security deposit <span className="text-muted">· refundable</span></td>
              <td className="text-end">{money(quote.depositAmount)}</td>
            </tr>
          )}
          <tr className="fw-bold border-top">
            <td>Total payable</td>
            <td className="text-end">
              {deliveryPending || quote.totalAmount == null ? <span className="text-muted">—</span> : money(quote.totalAmount)}
            </td>
          </tr>
        </tbody>
      </Table>
      {deliveryPending && (
        <div className="small text-warning">
          {quote.msg || 'Pick your delivery address from the suggestions or use "Detect my location" to see the delivery charge and total.'}
        </div>
      )}
      {!deliveryPending && quote.cashOnDelivery && (
        <div className="small text-muted">Cash on delivery — pay the total to the delivery agent on handover.</div>
      )}
      {/* v5_fix_issues #5 - car mileage terms (0 = no limit). */}
      {quote.includedKmPerDay > 0 && (
        <div className="small text-muted mt-1">
          Includes {quote.includedKmPerDay} km/day{quote.perKmRate > 0 ? ` · ₹${quote.perKmRate}/km beyond, charged on return` : ''}.
        </div>
      )}
      {/* v4_fix_issues #1/#3 - the owner-set minimum up-front amount. */}
      {!deliveryPending && quote.minPaymentAmount > 0 && quote.totalAmount != null && (
        <div className="small mt-1">
          <span className="text-muted">Pay at least </span>
          <strong>{money(quote.minPaymentAmount)}</strong>
          <span className="text-muted"> now to confirm; balance of {money(quote.totalAmount - quote.minPaymentAmount)} due later.</span>
        </div>
      )}
    </div>
  );
}
